import { useMemo, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeftOutlined, EditOutlined, StopOutlined, CalendarOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { message } from "antd";
import {
  convertToUserTimezone,
  DEFAULT_STAFF_TIMEZONE,
  formatTimezoneDisplay,
} from "../../../../utils/dateService";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import DisableStaffModal from "./DisableStaffModal/DisableStaffModal";
import ChangeEmailModal from "./ChangeEmailModal/ChangeEmailModal";
import ResetMfaModal from "./ResetMfaModal/ResetMfaModal";
import PermissionsTab from "./PermissionsTab/PermissionsTab";
import TimezoneDropdown from "../../../CommonComponents/TimezoneDropdown/TimezoneDropdown";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
  handleExpiredTokenError,
} from "../../../../utils/ErrorUtils";
import "./stylesStaffDetails.css";
import { useGetRoles } from "../../../../queries/useRolesApi";
import { apiRoleToUiRole } from "../../../../utils/roleUtils";
import { useGetStaffDetails } from "../../../../queries/useGetStaffDetails";
import { useResetStaffMfa } from "../../../../queries/useResetStaffMfa";
import { AGENT_ACTIONS } from "../../../../utils/Constants";
import AppContext from "../../../../context/AppContext";
import ActivityTab from "./ActivityTab/ActivityTab";

/** Normalize role name to group_name style (e.g. "Super Admin V2" -> "super-admin-v2", "customer_support" -> "customer-support"). */
function roleNameToGroupName(name) {
  if (!name || typeof name !== "string") return "";
  return name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/_/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

function StaffDetails({ staff, onBack, isPam2faEnabled = false }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const adminId = staff?.admin_id ?? staff?.id ?? null;

  const { data: apiRoles } = useGetRoles();
  const { data: detailsData } = useGetStaffDetails(adminId);
  const resetStaffMfaMutation = useResetStaffMfa();
  
  const roles = useMemo(() => apiRoles?.map(apiRoleToUiRole) ?? [], [apiRoles]);

  const [activeTab, setActiveTab] = useState("permissions");
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);
  const [isChangeEmailLoading, setIsChangeEmailLoading] = useState(false);
  const [isResetMfaModalOpen, setIsResetMfaModalOpen] = useState(false);
  const [localEmail, setLocalEmail] = useState(staff?.email ?? null);
  const [mfaEnrolled, setMfaEnrolled] = useState(staff?.mfaEnrolled);
  const [isResetPasswordLoading, setIsResetPasswordLoading] = useState(false);
  const [groupName, setGroupName] = useState(staff?.roleId || staff?.role || "");
  const [selectedRole, setSelectedRole] = useState("");
  const [originalRole, setOriginalRole] = useState("");
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [timezone, setTimezone] = useState(staff?.timezone || DEFAULT_STAFF_TIMEZONE);
  const [originalTimezone, setOriginalTimezone] = useState(staff?.timezone || DEFAULT_STAFF_TIMEZONE);
  const [showSavePersonalInfoButton, setShowSavePersonalInfoButton] = useState(false);
  const [isSaveRoleLoading, setIsSaveRoleLoading] = useState(false);
  const [isSavePersonalInfoLoading, setIsSavePersonalInfoLoading] = useState(false);
  const [localStatus, setLocalStatus] = useState(staff?.status || "");
  const [localStatusColor, setLocalStatusColor] = useState(staff?.status === "Deleted" ? "var(--red-500)" : staff?.statusColor || "");
  const [localDeactivationReason, setLocalDeactivationReason] = useState(staff?.deactivation_reason ?? "-");
  const { adminActions } = useContext(AppContext);
  const canEditPAMUser = adminActions?.includes(AGENT_ACTIONS.EDIT_PAM_USER);
  // Use originalRole (only updated after save), otherwise fall back to staff data
  const displayGroupName = originalRole || staff?.roleId || staff?.role || "";
  const roleForGroupName = useMemo(
    () =>
      roles.find(
        (r) =>
          r.id === displayGroupName ||
          roleNameToGroupName(r.name) === displayGroupName ||
          r.name === displayGroupName
      ),
    [roles, displayGroupName]
  );

  // Sync role dropdown from detailsData or staff data when roles load.
  // Match by normalized name so "customer-support" (API) matches "Customer Support" or "customer_support" (roles list).
  useEffect(() => {
    const roleOrGroup = detailsData?.role ?? detailsData?.group_name;
    if (roleOrGroup != null && roles.length > 0) {
      setGroupName(detailsData.group_name ?? detailsData.role ?? "");
      const normalizedFromApi = roleNameToGroupName(detailsData.role || detailsData.group_name);
      const matched = roles.find(
        (r) =>
          r.id === detailsData.roleId ||
          r.id === detailsData.role_id ||
          r.id === detailsData.role ||
          r.id === detailsData.group_name ||
          r.name === detailsData.role ||
          r.name === detailsData.group_name ||
          roleNameToGroupName(r.name) === normalizedFromApi
      );
      const roleId = matched?.id ?? detailsData.role ?? detailsData.group_name;
      setSelectedRole(roleId);
      setOriginalRole(roleId);
    } else if (roleOrGroup != null && roles.length === 0) {
      setGroupName(detailsData.group_name ?? detailsData.role ?? "");
      const roleId = detailsData.role ?? detailsData.group_name;
      setSelectedRole(roleId);
      setOriginalRole(roleId);
    } else if (staff?.role && roles.length > 0 && !selectedRole) {
      const normalizedStaffRole = roleNameToGroupName(staff.role);
      const fromStaff = roles.find(
        (r) =>
          r.id === staff.roleId ||
          r.label === staff.role ||
          r.name === staff.role ||
          roleNameToGroupName(r.name) === normalizedStaffRole
      );
      if (fromStaff) {
        setSelectedRole(fromStaff.id);
        setOriginalRole(fromStaff.id);
        setGroupName(fromStaff.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedRole omitted to avoid resetting user's dropdown selection
  }, [detailsData?.role, detailsData?.group_name, detailsData?.role_id, detailsData?.roleId, staff?.role, staff?.roleId, roles]);

  // Sync timezone from detailsData
  useEffect(() => {
    if (detailsData?.timezone != null) {
      setTimezone(detailsData.timezone);
      setOriginalTimezone(detailsData.timezone);
    }
  }, [detailsData?.timezone]);

  // Sync email from detailsData
  useEffect(() => {
    if (detailsData?.email != null) {
      setLocalEmail(detailsData.email);
    }
  }, [detailsData?.email]);

  useEffect(() => {
    if (typeof detailsData?.mfa_enrolled === "boolean") {
      setMfaEnrolled(detailsData.mfa_enrolled);
    }
  }, [detailsData?.mfa_enrolled]);

  // Sync status from detailsData
  useEffect(() => {
    if (detailsData?.status != null) {
      setLocalStatus(detailsData.status);
      const statusColor = detailsData.status === "Deleted" ? "var(--red-500)" : detailsData.status_color || "";
      setLocalStatusColor(statusColor);
    }
    if (detailsData?.deactivation_reason != null) {
      setLocalDeactivationReason(detailsData.deactivation_reason);
    }
  }, [detailsData?.status, detailsData?.status_color, detailsData?.deactivation_reason]);

  // Generate initials for avatar
  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const handleResetPassword = async () => {
    const token = JSON.parse(localStorage.getItem("token"));
    const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(token?.AccessToken);
    const adminId = staff.admin_id ?? staff.id;
    const url = API_ROUTES?.STAFF_RESET_PASSWORD?.replace("{admin_id}", adminId);
    setIsResetPasswordLoading(true);
    try {
      const response = await PrivateAdminPanelInstance?.patch(url);
      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );
    } catch (error) {
      const data = error?.response?.data;
      const errorCode = data?.error_code;
      if (errorCode === "ADMIN_NOT_FOUND") {
        message.error(AppStrings.PAM_STAFF_RESET_PASSWORD_ADMIN_NOT_FOUND);
      } else {
        message.error(
          getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED)
        );
      }
      handleExpiredTokenError(error?.response, navigate);
    } finally {
      setIsResetPasswordLoading(false);
    }
  };

  const handleDisable = () => {
    setIsDisableModalOpen(true);
  };

  const handleResetMfaConfirm = async () => {
    if (!adminId) return;

    try {
      await resetStaffMfaMutation.mutateAsync(adminId);
      setMfaEnrolled(false);
      setIsResetMfaModalOpen(false);
      message.success(AppStrings.PAM_STAFF_RESET_MFA_SUCCESS);
      await queryClient.invalidateQueries({
        queryKey: ["GET_STAFF_DETAILS", adminId],
      });
      await queryClient.refetchQueries({
        queryKey: ["GET_STAFF"],
        type: "all",
      });
    } catch (error) {
      const errorCode = error?.response?.data?.error_code;
      message.error(
        errorCode === "STAFF_MFA_RESET_ERROR"
          ? AppStrings.PAM_STAFF_RESET_MFA_FAILED
          : AppStrings.PAM_GENERIC_REQUEST_FAILED
      );
      handleExpiredTokenError(error?.response, navigate);
    }
  };

  const handleChangeEmailConfirm = async (newEmail) => {
    const token = JSON.parse(localStorage.getItem("token"));
    const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(token?.AccessToken);
    const url = API_ROUTES?.STAFF_CHANGE_EMAIL?.replace("{admin_id}", adminId);
    setIsChangeEmailLoading(true);
    try {
      await PrivateAdminPanelInstance?.patch(url, { new_email: newEmail });
      setLocalEmail(newEmail);
      setIsChangeEmailModalOpen(false);
      message.success(AppStrings.STAFF_EMAIL_UPDATED_SUCCESSFULLY);
      await queryClient.invalidateQueries({ queryKey: ["GET_STAFF_DETAILS", adminId ?? ""] });
      await queryClient.refetchQueries({
        queryKey: ["GET_STAFF"],
        type: "all",
      });
    } catch (error) {
      const data = error?.response?.data;
      const errorCode = data?.error_code;
      if (errorCode === "EMAIL_ALREADY_IN_USE") {
        message.error(AppStrings.STAFF_EMAIL_ALREADY_IN_USE);
      } else if (errorCode === "STAFF_ACCOUNT_PROTECTED") {
        message.error(AppStrings.STAFF_ACCOUNT_PROTECTED_EMAIL);
      } else {
        message.error(
          getApiErrorMessage(error, AppStrings.FAILED_TO_CHANGE_STAFF_EMAIL)
        );
      }
      handleExpiredTokenError(error?.response, navigate);
    } finally {
      setIsChangeEmailLoading(false);
    }
  };

  const handleDisableConfirm = async (reason, comment) => {
    const token = JSON.parse(localStorage.getItem("token"));
    const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(token?.AccessToken);
    const url = API_ROUTES?.DISABLE_STAFF?.replace("{admin_id}", adminId);
    
    try {
      const response = await PrivateAdminPanelInstance?.patch(url, { deactivation_reason: comment });
      const updatedStaff = response?.data?.data || response?.data

      // Update local state with response data
      if (updatedStaff?.status) {
        setLocalStatus(updatedStaff.status);
        setLocalDeactivationReason(updatedStaff.deactivation_reason);
      }
      if (updatedStaff?.status_color) {
        const statusColor = updatedStaff.status === "Deleted" ? "var(--red-500)" : updatedStaff.status_color;
        setLocalStatusColor(statusColor);
      }
      setIsDisableModalOpen(false);
      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );
      await queryClient.invalidateQueries({ queryKey: ["GET_STAFF_DETAILS", adminId ?? ""] });
      await queryClient.refetchQueries({
        queryKey: ["GET_STAFF"],
        type: "all",
      });
    } catch (error) {
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
      handleExpiredTokenError(error?.response, navigate);
    }
  };

  const handleDisableCancel = () => {
    setIsDisableModalOpen(false);
  };

  const getEditAdminUrl = () => API_ROUTES?.UPDATE_STAFF?.replace("{admin_id}", adminId);

  const handleRoleChange = (value) => {
    setSelectedRole(value);
    setShowSaveButton(value !== originalRole);
  };

  const handleSaveRoleChange = async () => {
    const token = JSON.parse(localStorage.getItem("token"));
    const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(token?.AccessToken);
    const roleForSelected = roles.find((r) => r.id === selectedRole);
    const roleIdToSend = roleForSelected?.id ?? selectedRole;
    const groupNameToSend = roleForSelected
      ? roleNameToGroupName(roleForSelected.name) || roleForSelected.id
      : selectedRole;
    setIsSaveRoleLoading(true);
    try {
      const response = await PrivateAdminPanelInstance?.patch(getEditAdminUrl(), {
        role_id: roleIdToSend,
      });
      setGroupName(groupNameToSend);
      setOriginalRole(selectedRole);
      setShowSaveButton(false);
      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );
      await queryClient.invalidateQueries({ queryKey: ["GET_STAFF_DETAILS", adminId ?? ""] });
      await queryClient.refetchQueries({
        queryKey: ["GET_STAFF"],
        type: "all",
      });
    } catch (error) {
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
      handleExpiredTokenError(error?.response, navigate);
    } finally {
      setIsSaveRoleLoading(false);
    }
  };

  const handleEditPersonalInfo = () => {
    setIsEditMode(true);
  };

  const handleTimezoneChange = (timezoneKey) => {
    setTimezone(timezoneKey);
    setShowSavePersonalInfoButton(timezoneKey !== originalTimezone);
  };

  const handleSavePersonalInfo = async () => {
    const token = JSON.parse(localStorage.getItem("token"));
    const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(token?.AccessToken);
    const payload = {
      timezone: timezone || "",
      group_name: groupName || roleNameToGroupName(roles.find((r) => r.id === selectedRole)?.name) || selectedRole,
    };
    setIsSavePersonalInfoLoading(true);
    try {
      const response = await PrivateAdminPanelInstance?.patch(getEditAdminUrl(), payload);
      setOriginalTimezone(timezone);
      setShowSavePersonalInfoButton(false);
      setIsEditMode(false);
      message.success(
        getApiSuccessMessage(
          response,
          AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED
        )
      );
      await queryClient.invalidateQueries({ queryKey: ["GET_STAFF_DETAILS", adminId ?? ""] });
      await queryClient.refetchQueries({
        queryKey: ["GET_STAFF"],
        type: "all",
      });
    } catch (error) {
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
      handleExpiredTokenError(error?.response, navigate);
    } finally {
      setIsSavePersonalInfoLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setTimezone(originalTimezone);
    setIsEditMode(false);
    setShowSavePersonalInfoButton(false);
  };

  const expirationDate = staff?.expirationDate || null;
  const displayRole = roleForGroupName?.label ?? roleForGroupName?.name ?? displayGroupName ?? staff?.role ?? "-";
  const staffDisplayName =
    `${staff?.firstName ?? ""} ${staff?.lastName ?? ""}`.trim() ||
    localEmail ||
    "-";

  return (
    <div className="staff-details-container">
      <div className="staff-details-title">
        <button className="staff-details-back-button" onClick={onBack}>
          <ArrowLeftOutlined />
        </button>
        <h2>{AppStrings.BACK_TO_STAFF}</h2>
      </div>

      <div className="staff-details-content">
        <div className="staff-details-left-panel">
          <div className="staff-details-header">
            <span
              className="staff-details-status-badge"
              style={{ backgroundColor: localStatusColor || staff?.statusColor }}
            >
              {localStatus || staff?.status || "-"}
            </span>
            <div className="staff-details-avatar">
              {getInitials(staff?.firstName, staff?.lastName)}
            </div>
            <h2 className="staff-details-name">
              {staff?.firstName} {staff?.lastName}
            </h2>
            <div className="staff-details-role">
              <span
                className="staff-details-role-dot"
                style={{ backgroundColor: roleForGroupName?.color ?? staff?.roleColor }}
              />
              <span>{displayRole}</span>
            </div>
            {expirationDate && (
              <div className="staff-details-expiration-date">
                <CalendarOutlined />
                <span>Exp. date: {convertToUserTimezone(expirationDate, "DD MMM, YYYY")}</span>
              </div>
            )}
          </div>

          <div className="staff-details-personal-info">
            <div className="staff-details-section-header">
              <h3>{AppStrings.PERSONAL_INFO}</h3>
              {!isEditMode ? (
                <button className={`staff-details-edit-button ${localStatus === "Deleted" || !canEditPAMUser ? "disabled" : ""}`} onClick={handleEditPersonalInfo} disabled={localStatus === "Deleted" || !canEditPAMUser}>
                  <EditOutlined />
                  {AppStrings.EDIT}
                </button>
              ) : (
                <button className="staff-details-edit-button" onClick={handleCancelEdit}>
                  Cancel
                </button>
              )}
            </div>
            <div className="staff-details-info-item">
              <span className="staff-details-info-label">{AppStrings.EMAIL}:</span>
              <span className="staff-details-info-value">{localEmail ?? staff?.email ?? "-"}</span>
              {localStatus !== "Deleted" && canEditPAMUser && (
                <button
                  type="button"
                  className="staff-details-change-email-button"
                  aria-label={AppStrings.CHANGE_EMAIL}
                  title={AppStrings.CHANGE_EMAIL}
                  onClick={() => setIsChangeEmailModalOpen(true)}
                >
                  <EditOutlined />
                </button>
              )}
            </div>
            <div className="staff-details-info-item">
              <span className="staff-details-info-label">{AppStrings.ID}:</span>
              <span className="staff-details-info-value">{staff?.id ?? adminId ?? "-"}</span>
            </div>
            {isPam2faEnabled && (
              <div className="staff-details-info-item">
                <span className="staff-details-info-label">
                  {AppStrings.PAM_STAFF_2FA_STATUS}:
                </span>
                <span
                  className={`staff-details-mfa-status ${
                    mfaEnrolled === true
                      ? "staff-details-mfa-status--enrolled"
                      : mfaEnrolled === false
                        ? "staff-details-mfa-status--not-enrolled"
                        : ""
                  }`}
                >
                  {mfaEnrolled === true
                    ? AppStrings.PAM_STAFF_2FA_ENROLLED
                    : mfaEnrolled === false
                      ? AppStrings.PAM_STAFF_2FA_NOT_ENROLLED
                      : "-"}
                </span>
              </div>
            )}
            <div className="staff-details-info-item">
              <span className="staff-details-info-label">Timezone:</span>
              {isEditMode ? (
                <div style={{ flex: 1 }}>
                  <TimezoneDropdown
                    selectedTimezone={timezone}
                    onTimezoneChange={handleTimezoneChange}
                    className="staff-details-timezone-dropdown"
                  />
                </div>
              ) : (
                <span className="staff-details-info-value">
                  {timezone ? formatTimezoneDisplay(timezone) : "-"}
                </span>
              )}
            </div>
            {isEditMode && showSavePersonalInfoButton && (
              <div className="staff-details-save-personal-info-button-container">
                <button
                  className="staff-details-save-personal-info-button"
                  onClick={handleSavePersonalInfo}
                  disabled={isSavePersonalInfoLoading}
                >
                  {isSavePersonalInfoLoading ? "Saving…" : "Save Changes"}
                </button>
              </div>
            )}
          </div>

          {localStatus !== "Deleted" && (
            <div className="staff-details-actions">
              <div className="staff-details-actions-buttons">
                <button className={`staff-details-disable-button staff-details-disable-button--full ${localStatus === "Deleted" || !canEditPAMUser ? "disabled" : ""}`} onClick={handleDisable} disabled={localStatus === "Deleted" || !canEditPAMUser}>
                  <StopOutlined />
                  Disable
                </button>
              </div>
              <button
                type="button"
                className={`staff-details-reset-password-button ${isResetPasswordLoading || !canEditPAMUser ? "disabled" : ""}`}
                onClick={handleResetPassword}
                disabled={isResetPasswordLoading || !canEditPAMUser}
              >
                {isResetPasswordLoading ? "Sending…" : "Reset password"}
              </button>
              {isPam2faEnabled && (
                <button
                  type="button"
                  className={`staff-details-reset-password-button ${resetStaffMfaMutation.isPending || !canEditPAMUser ? "disabled" : ""}`}
                  onClick={() => setIsResetMfaModalOpen(true)}
                  disabled={
                    resetStaffMfaMutation.isPending ||
                    !canEditPAMUser ||
                    !adminId ||
                    mfaEnrolled === false
                  }
                >
                  {AppStrings.PAM_STAFF_RESET_MFA}
                </button>
              )}
            </div>
          )}

        {localStatus === "Deleted" && (
          <div className="staff-details-deleted-message">
            <div className="staff-details-deleted-message-header">
              <CloseCircleOutlined className="warning-icon" />
              <p>{AppStrings.DELETED_REASON}</p>
            </div>
            <p className="staff-details-deleted-message-text">{localDeactivationReason ?? "-"}</p>
          </div>
        )}
        </div>

        <div className="staff-details-right-panel">
          <div className="staff-details-tabs">
            <button
              className={`staff-details-tab ${activeTab === "permissions" ? "active" : ""}`}
              onClick={() => setActiveTab("permissions")}
            >
              {AppStrings.PERMISSIONS}
            </button>
            <button
              className={`staff-details-tab ${activeTab === "activity" ? "active" : ""}`}
              onClick={() => setActiveTab("activity")}
            >
              {AppStrings.ACTIVITY}
            </button>
          </div>

          {activeTab === "permissions" && (
            <PermissionsTab 
              staff={staff} 
              role={selectedRole}
              onRoleChange={handleRoleChange}
              showSaveButton={showSaveButton}
              onSaveRoleChange={handleSaveRoleChange}
              isSaveRoleLoading={isSaveRoleLoading}
              localStatus={localStatus}
              canEditPAMUser={canEditPAMUser}
            />
          )}
          {activeTab === "activity" && (
            <ActivityTab staffId={adminId} />
          )}
        </div>
      </div>

      <DisableStaffModal
        isOpen={isDisableModalOpen}
        onConfirm={handleDisableConfirm}
        onCancel={handleDisableCancel}
        staffName={`${staff?.firstName ?? ""} ${staff?.lastName ?? ""}`.trim() || "Staff"}
      />

      <ChangeEmailModal
        isOpen={isChangeEmailModalOpen}
        onConfirm={handleChangeEmailConfirm}
        onCancel={() => setIsChangeEmailModalOpen(false)}
        currentEmail={localEmail ?? staff?.email}
        isLoading={isChangeEmailLoading}
      />

      {isPam2faEnabled && (
        <ResetMfaModal
          isOpen={isResetMfaModalOpen}
          onConfirm={handleResetMfaConfirm}
          onCancel={() => setIsResetMfaModalOpen(false)}
          staffName={staffDisplayName}
          isLoading={resetStaffMfaMutation.isPending}
        />
      )}

    </div>
  );
}

export default StaffDetails;

